import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney } from '@theme/utilities';

const DISABLE_SECTION_RENDER = false; // AJAX section render for filters (no full page reload)
const FACET_SCROLL_FLAG = 'facetsScrollToTop';

/**
 * Filter types that use OR logic (multi-select siblings).
 * Shared between module-level helpers and the OR-filter IIFE.
 * @type {readonly string[]}
 */
const OR_FILTER_PATTERNS = [
  'pattern',
  'finish',
  'style',
];

/** Filters that should never be converted to numeric sliders */
const SLIDER_EXCLUDE_PATTERNS = ['material', 'pattern', 'finish', 'style', 'color', 'colour'];

/**
 * Check whether a filter param name corresponds to an OR-style filter
 * that supports sibling restoration / multi-select.
 * @param {string} filterParamName - The dashed filter param name (data-filter-param-name value)
 * @returns {boolean}
 */
function isORStyleFilter(filterParamName) {
  const lower = filterParamName.toLowerCase();
  if (lower.includes('color-pattern') || lower.includes('colour-pattern') || lower.includes('color') || lower.includes('colour')) {
    return false;
  }
  return OR_FILTER_PATTERNS.some((pattern) => lower.includes(pattern));
}

/**
 * Check whether a filter should be excluded from numeric slider conversion.
 * @param {string} paramName
 * @returns {boolean}
 */
function isSliderExcluded(paramName) {
  const lower = paramName.toLowerCase();
  return SLIDER_EXCLUDE_PATTERNS.some((p) => lower.includes(p));
}

/**
 * Cache of available filter options per filter group.
 * Stores which options were available BEFORE a sibling was selected,
 * so siblings can be re-enabled after morph.
 * @type {Map<string, Set<string>>}
 */
const availableOptionsCache = new Map();

/**
 * Cache of original option order per filter group.
 * Stores the ordered list of input values so swatches stay in their
 * original position after morph reorders them by relevance.
 * @type {Map<string, string[]>}
 */
const originalOrderCache = new Map();

/**
 * Global busy lock for facet filter updates.
 * Set immediately in the capture-phase click handler so it blocks before any
 * async work begins. Cleared after morph completes, on preflight abort, or
 * before hard navigation.
 * @type {boolean}
 */
let facetsBusy = false;

/**
 * Why the lock was taken. 'swatch' means the capture-phase click handler set
 * it and updateFilters() should still proceed (it's the initiating call).
 * Any other truthy value means a real in-flight update — block everything.
 * @type {'swatch'|'update'|null}
 */
let facetsBusyReason = null;

/**
 * Sets the global busy flag and mirrors it as a data attribute on every
 * facets-form-component so CSS can show a visual cue.
 * @param {boolean} busy
 * @param {'swatch'|'update'|null} [reason=null]
 */
function setFacetsBusy(busy, reason = null) {
  facetsBusy = busy;
  facetsBusyReason = busy ? (reason || 'update') : null;
  document.querySelectorAll('facets-form-component').forEach((el) => {
    if (busy) {
      /** @type {HTMLElement} */ (el).dataset.facetsBusy = 'true';
    } else {
      delete /** @type {HTMLElement} */ (el).dataset.facetsBusy;
    }
  });
}

/**
 * Build a context-aware cache key for a filter group.
 * The key includes the filter's own param name plus all OTHER active URL
 * filter params (sorted for stability).  This means the cache automatically
 * invalidates when any other filter (e.g. brand) changes.
 *
 * @param {string} filterParamName - The dashed filter name from data-filter-param-name
 * @param {HTMLElement} group - The accordion-custom element for this filter group
 * @returns {string} e.g. "color-pattern::filter.p.vendor=BrandX&filter.v.price.gte=100"
 */
function buildFilterContextKey(filterParamName, group) {
  // Get the real URL param name from the first checkbox (dots, not dashes)
  const firstInput = group.querySelector('input[type="checkbox"]');
  const urlParamName = firstInput instanceof HTMLInputElement ? firstInput.name : '';

  // Build context string from all URL params EXCEPT this filter's own values
  const params = new URLSearchParams(window.location.search);
  if (urlParamName) params.delete(urlParamName);

  const sorted = [...params.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));

  return `${filterParamName}::${sorted.map(([k, v]) => `${k}=${v}`).join('&')}`;
}

/**
 * Build a URL representing the current filter state but with one filter's params removed.
 * Used to fetch "what options would be available if this filter wasn't selected?"
 * @param {HTMLElement} group - The accordion-custom element for the filter to exclude
 * @returns {URL}
 */
function buildContextURLWithoutFilter(group) {
  const firstInput = group.querySelector('input[type="checkbox"]');
  const urlParamName = firstInput instanceof HTMLInputElement ? firstInput.name : '';

  const url = new URL(window.location.href);
  if (urlParamName) url.searchParams.delete(urlParamName);
  return url;
}

/**
 * Parse available (enabled) filter options and full order from section HTML
 * for a specific filter group.
 * @param {string} html - Section HTML from Section Rendering API
 * @param {string} filterParamName - The dashed filter param name (data-filter-param-name value)
 * @returns {{ available: Set<string>, order: string[] } | null}
 */
function parseFilterOptionsFromHTML(html, filterParamName) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Prefer the desktop accordion (not inside .facets--drawer); fall back to first match
  const allMatches = doc.querySelectorAll(`accordion-custom[data-filter-param-name="${filterParamName}"]`);
  if (allMatches.length === 0) return null;

  let group = null;
  for (const match of allMatches) {
    if (!match.closest('.facets--drawer')) {
      group = match;
      break;
    }
  }
  if (!group) group = /** @type {Element} */ (allMatches[0]);

  /** @type {Set<string>} */
  const available = new Set();
  /** @type {string[]} */
  const order = [];

  /** @type {NodeListOf<HTMLInputElement>} */
  const allInputs = group.querySelectorAll('input[type="checkbox"]');

  allInputs.forEach((input) => {
    order.push(input.value);

    const fieldset = input.closest('fieldset');
    const isDisabledByClass = fieldset?.classList.contains('variant-option--swatches-disabled');
    const isDisabledByAttr = input.disabled;
    if (!isDisabledByAttr && !isDisabledByClass) {
      available.add(input.value);
    }
  });

  return available.size > 0 || order.length > 0 ? { available, order } : null;
}

/**
 * Gets the main facets container that has filter accordions (not the drawer).
 * @returns {Element | null}
 */
function getMainFacetsContainer() {
  const vertical = document.querySelector('.facets--vertical:not(.facets--drawer)');
  if (vertical && vertical.querySelectorAll('accordion-custom[data-filter-param-name]').length > 0) {
    return vertical;
  }

  const horizontal = document.querySelector('.facets--horizontal:not(.facets--drawer)');
  if (horizontal && horizontal.querySelectorAll('accordion-custom[data-filter-param-name]').length > 0) {
    return horizontal;
  }

  const any = document.querySelector('.facets:not(.facets--drawer)');
  if (any && any.querySelectorAll('accordion-custom[data-filter-param-name]').length > 0) {
    return any;
  }

  return vertical || horizontal || any;
}

/**
 * Gets the mobile drawer facets container, if present and populated.
 * @returns {Element | null}
 */
function getDrawerFacetsContainer() {
  const drawer = document.querySelector('.facets--drawer');
  if (drawer && drawer.querySelectorAll('accordion-custom[data-filter-param-name]').length > 0) {
    return drawer;
  }
  return null;
}

/**
 * Returns an array of all facets containers that need cache/restore treatment.
 * Includes the main desktop container and the mobile drawer (if present).
 * @returns {Element[]}
 */
function getAllFacetsContainers() {
  /** @type {Element[]} */
  const containers = [];
  const main = getMainFacetsContainer();
  if (main) containers.push(main);
  const drawer = getDrawerFacetsContainer();
  if (drawer) containers.push(drawer);
  return containers;
}

/**
 * After morph: Capture available options for filter groups with NO active selection.
 * This stores the "pristine" state so we can restore siblings later.
 * Cache keys include the surrounding filter state so entries auto-invalidate
 * when other filters (e.g. brand) change.
 *
 * @param {Element} container - The facets container to process
 */
function captureAvailableOptions(container) {

  /** @type {NodeListOf<HTMLElement>} */
  const filterGroups = container.querySelectorAll('accordion-custom[data-filter-param-name]');

  filterGroups.forEach(group => {
    const filterName = group.dataset.filterParamName;
    if (!filterName) return;

    const cacheKey = buildFilterContextKey(filterName, group);
    const hasActiveValue = group.querySelector('input[type="checkbox"]:checked') !== null;

    // Only capture if this group has NO active selection
    if (!hasActiveValue) {
      const options = new Set();
      /** @type {NodeListOf<HTMLInputElement>} */
      const allInputs = group.querySelectorAll('input[type="checkbox"]');
      allInputs.forEach((input) => {
        const fieldset = input.closest('fieldset');
        const isDisabledByClass = fieldset?.classList.contains('variant-option--swatches-disabled');
        const isDisabledByAttr = input.disabled;
        if (!isDisabledByAttr && !isDisabledByClass) {
          options.add(input.value);
        }
      });

      if (options.size > 0) {
        availableOptionsCache.set(cacheKey, options);
      }

      // Capture original order (all values, including disabled) for this context
      if (!originalOrderCache.has(cacheKey) && allInputs.length > 0) {
        /** @type {string[]} */
        const order = [];
        allInputs.forEach((input) => order.push(input.value));
        originalOrderCache.set(cacheKey, order);
      }
    }
  });
}

/**
 * After morph: Enable OR-style filter swatches so they remain visible and clickable.
 *
 * For OR-style filter groups (color, material, finish, etc.):
 * - If a context cache exists for this filter (from the "brand-only" fetch), only
 *   enable values that appear in that cache.
 * - If no cache exists, fall back to enabling ALL values in the group (first load,
 *   or before any selection has been made).
 *
 * Enabled swatches get:
 * - `disabled` attribute removed so clicks register
 * - `variant-option--swatches-disabled` class removed so they are visible
 * - `or-sibling-enabled` class added so muted-but-clickable CSS applies
 * - `data-cached-sibling="true"` marker for the delegated click handler
 *
 * Swatches that were disabled by Shopify (0 results alone) keep a muted visual
 * style via the `.or-sibling-enabled` CSS rule (reduced opacity + strikethrough).
 *
 * Non-OR filters are left untouched.
 *
 * @param {Element} container - The facets container to process
 */
function restoreSiblingOptions(container) {
  /** @type {NodeListOf<HTMLElement>} */
  const filterGroups = container.querySelectorAll('accordion-custom[data-filter-param-name]');

  filterGroups.forEach(group => {
    const filterName = group.dataset.filterParamName;
    if (!filterName) return;

    // Only touch OR-style filters
    if (!isORStyleFilter(filterName)) return;

    /** @type {NodeListOf<HTMLInputElement>} */
    const allInputs = group.querySelectorAll('input[type="checkbox"]');

    allInputs.forEach((input) => {
      const fieldset = input.closest('fieldset');
      const isDisabledByClass = fieldset?.classList.contains('variant-option--swatches-disabled');
      const isDisabledByAttr = input.disabled;
      const isDisabled = isDisabledByAttr || isDisabledByClass;

      if (!isDisabled) return; // Already enabled — nothing to do

      // For OR-style filters, always enable every swatch regardless of cache.
      // Users should always be able to click any colour/material/etc.
      input.disabled = false;
      input.removeAttribute('disabled');
      input.dataset.cachedSibling = 'true';
      input.classList.add('or-filter-enabled');

      if (fieldset) {
        fieldset.classList.remove('variant-option--swatches-disabled');
        fieldset.classList.add('or-sibling-enabled');
      }
    });
  });
}

/**
 * Resolve the checkbox input from a click target inside a filter swatch.
 * Clicks may land on the <input> itself, or on a <label>, swatch <span>,
 * strikethrough <svg>, or the wrapping <fieldset>.  closest() only walks UP
 * the tree, so for swatch markup where the input is a sibling/descendant of
 * the click target we need querySelector fallbacks.
 *
 * @param {HTMLElement} target - event.target
 * @returns {HTMLInputElement | null}
 */
function resolveSwatchInput(target) {
  // 1. Direct hit — click landed on the input itself
  const direct = target.closest('input[type="checkbox"]');
  if (direct) return /** @type {HTMLInputElement} */ (direct);

  // 2. Click on a label or swatch element — find the input inside the fieldset
  const fieldset = target.closest('fieldset');
  if (fieldset) {
    const input = fieldset.querySelector('input[type="checkbox"]');
    if (input) return /** @type {HTMLInputElement} */ (input);
  }

  // 3. Click on a wrapping label (outside fieldset but still in the list item)
  const label = target.closest('label');
  if (label) {
    const input = label.querySelector('input[type="checkbox"]');
    if (input) return /** @type {HTMLInputElement} */ (input);
  }

  return null;
}

/**
 * Apply single-select logic for an OR-style swatch filter input.
 * Unchecks all other checked checkboxes in every matching filter group
 * (same data-filter-param-name) across ALL containers (desktop + drawer)
 * so the two stay in sync.
 *
 * If the input is about to be checked (not already checked), siblings are
 * cleared. If the input is already checked (user deselects), nothing is done.
 *
 * @param {HTMLInputElement} input - The checkbox being toggled
 * @returns {boolean} true if single-select was applied, false if skipped (deselect case)
 */
function applySingleSelectForOR(input) {
  const group = input.closest('accordion-custom[data-filter-param-name]');
  if (!group) return false;
  const filterName = /** @type {HTMLElement} */ (group).dataset.filterParamName;
  if (!filterName || !isORStyleFilter(filterName)) return false;

  // If the input is still disabled but its fieldset is an OR-sibling, unblock it
  if (input.disabled) {
    const fieldset = input.closest('fieldset');
    if (fieldset?.classList.contains('or-sibling-enabled')) {
      input.disabled = false;
      input.removeAttribute('disabled');
    } else {
      return false; // Genuinely disabled — don't interfere
    }
  }

  // Deselect case — clicking an already-checked swatch clears it
  if (input.checked) {
    input.checked = false;
    return true;
  }

  // Uncheck all checked siblings in EVERY container that has this filter group
  const selector = `accordion-custom[data-filter-param-name="${filterName}"] input[type="checkbox"]:checked`;
  document.querySelectorAll(selector).forEach((/** @type {HTMLInputElement} */ sibling) => {
    sibling.checked = false;
  });

  // Explicitly check the clicked swatch — don't rely on the native toggle,
  // which may not fire reliably for re-enabled OR swatches.
  input.checked = true;

  // Clear cached-sibling marker
  delete input.dataset.cachedSibling;
  return true;
}

/**
 * Delegated click handler for OR-style swatch filter groups.
 * Enforces single-select: clicking a swatch unchecks all others in the same
 * filter group, then lets the native click check the target.
 *
 * Uses the capture phase so we can uncheck siblings BEFORE the browser toggles
 * the clicked checkbox. This way only one `change` event fires on the
 * facet-inputs-component, avoiding a double filter update.
 */
document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);

  // Global busy lock — block ALL swatch clicks while a filter update is in flight
  if (facetsBusy) {
    const input = resolveSwatchInput(target);
    if (input) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    return;
  }

  const input = resolveSwatchInput(target);
  if (!input) return;

  const handled = applySingleSelectForOR(input);

  if (handled) {
    // We already set input.checked — prevent the native click from toggling it
    // back, and dispatch a change event so the normal on:change binding fires.
    event.preventDefault();
    input.dispatchEvent(new Event('change', { bubbles: true }));
    // Lock immediately, before the async updateFilters() runs.
    // Reason 'swatch' tells updateFilters() this is the initiating call — proceed.
    setFacetsBusy(true, 'swatch');
  }
}, true);

/**
 * Delegated change fallback.
 *
 * The `on:change="/updateFilters"` binding on <facet-inputs-component> can
 * break after a DOM morph.  This listener only fires when the checkbox is
 * NOT inside a <facet-inputs-component> — i.e. when the normal binding is
 * missing.  If the element IS inside one, the built-in handler will fire
 * and we skip to avoid duplicate calls.
 */
document.addEventListener('change', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;
  if (!target.closest('.facets')) return;

  // Skip custom in-stock toggle — handled by its own listener, not Shopify filters
  if (target.name === 'custom-in-stock') return;

  // If the checkbox is inside a facet-inputs-component the built-in
  // on:change="/updateFilters" binding handles it — bail out.
  if (target.closest('facet-inputs-component')) return;

  const facetsForm = /** @type {FacetsFormComponent|null} */ (target.closest('facets-form-component'));
  if (!facetsForm) return;

  facetsForm.updateFilters();
});

/**
 * After morph: Restore the original visual order of filter options using CSS.
 * Sets CSS order and flex on the parent <ul> so items appear in their original
 * positions without moving DOM nodes (which would interfere with the morph).
 *
 * @param {Element} container - The facets container to process
 */
function restoreOptionOrder(container) {

  /** @type {NodeListOf<HTMLElement>} */
  const filterGroups = container.querySelectorAll('accordion-custom[data-filter-param-name]');

  filterGroups.forEach(group => {
    const filterName = group.dataset.filterParamName;
    if (!filterName) return;

    const cacheKey = buildFilterContextKey(filterName, group);
    const cachedOrder = originalOrderCache.get(cacheKey);
    if (!cachedOrder) return;

    const list = /** @type {HTMLElement | null} */ (group.querySelector('ul.facets__inputs-list'));
    if (!list) return;

    // Ensure the list is a flex container so CSS order works
    if (!list.style.display) {
      list.style.display = 'flex';
      list.style.flexWrap = 'wrap';
    }

    // Build a lookup: value → original position index
    /** @type {Map<string, number>} */
    const orderMap = new Map();
    cachedOrder.forEach((/** @type {string} */ value, /** @type {number} */ index) => orderMap.set(value, index));

    /** @type {NodeListOf<HTMLElement>} */
    const items = list.querySelectorAll(':scope > li.facets__inputs-list-item');
    items.forEach(li => {
      const input = li.querySelector('input[type="checkbox"]');
      if (input instanceof HTMLInputElement) {
        const pos = orderMap.get(input.value);
        li.style.order = pos !== undefined ? String(pos) : '9999';
      }
    });
  });
}

/**
 * Lock the sidebar height before morph to prevent layout jump.
 * Sets min-height to current height so it can't collapse during the morph.
 */
function lockSidebarHeight() {
  const container = /** @type {HTMLElement | null} */ (getMainFacetsContainer());
  if (container) {
    container.style.minHeight = container.offsetHeight + 'px';
  }
}

/**
 * Release the sidebar height lock after morph + restoration is complete.
 */
function unlockSidebarHeight() {
  const container = /** @type {HTMLElement | null} */ (getMainFacetsContainer());
  if (container) {
    container.style.minHeight = '';
  }
}

/**
 * For filter groups WITH active selections, fetch the "context HTML" — the section
 * rendered with all current params EXCEPT that filter's own values.  This tells us
 * which options would be available if the user hadn't selected anything in that group,
 * given the current state of all other filters (brand, price, etc.).
 *
 * The parsed options are stored in availableOptionsCache / originalOrderCache under
 * the context-aware key so restoreSiblingOptions() can re-enable siblings correctly.
 *
 * @param {string} sectionId - Shopify section ID for the Section Rendering API
 * @param {Element} container - The facets container to process
 */
async function refreshCacheForActiveFilters(sectionId, container) {

  /** @type {NodeListOf<HTMLElement>} */
  const filterGroups = container.querySelectorAll('accordion-custom[data-filter-param-name]');

  /** @type {Promise<void>[]} */
  const fetchPromises = [];

  filterGroups.forEach((group) => {
    const filterName = group.dataset.filterParamName;
    if (!filterName) return;

    const hasActiveValue = group.querySelector('input[type="checkbox"]:checked') !== null;
    if (!hasActiveValue) return; // Already handled synchronously by captureAvailableOptions

    // Only context-fetch for OR-style filters (color, material, finish, etc.)
    // to avoid unnecessary network requests for brand/type/vendor filters.
    if (!isORStyleFilter(filterName)) return;

    const contextUrl = buildContextURLWithoutFilter(group);
    const cacheKey = buildFilterContextKey(filterName, group);

    const promise = sectionRenderer
      .getSectionHTML(sectionId, false, contextUrl)
      .then((html) => {
        const parsed = parseFilterOptionsFromHTML(html, filterName);
        if (parsed) {
          if (parsed.available.size > 0) {
            availableOptionsCache.set(cacheKey, parsed.available);
          }
          if (parsed.order.length > 0) {
            originalOrderCache.set(cacheKey, parsed.order);
          }
        }
      })
      .catch((err) => {
        console.debug(`[Facets] Failed to refresh cache for ${filterName}:`, err);
      });

    fetchPromises.push(promise);
  });

  await Promise.all(fetchPromises);
}

/**
 * Called after section morph completes.
 * Runs cache/restore for BOTH desktop and mobile drawer containers.
 * 1. Captures fresh availability for groups with NO active selection (sync, from DOM).
 * 2. For groups WITH active selections, fetches context HTML and rebuilds caches (async).
 * 3. Restores siblings and order using the (now up-to-date) caches.
 *
 * @param {string} sectionId - Shopify section ID for context-fetches
 */
async function handlePostMorph(sectionId) {
  const containers = getAllFacetsContainers();

  // 1. Capture availability for groups with NO active selection (sync, from DOM)
  for (const container of containers) {
    captureAvailableOptions(container);
  }

  // 2. For groups WITH active selections, fetch context HTML and rebuild caches
  //    (only needs one fetch per filter — caches are shared across containers)
  //    Use the first container to drive the fetch; caches are keyed by context, not container.
  if (containers.length > 0) {
    await refreshCacheForActiveFilters(sectionId, /** @type {Element} */ (containers[0]));
  }

  // 3. Restore siblings and order for all containers using (now current) caches
  for (const container of containers) {
    restoreSiblingOptions(container);
    restoreOptionOrder(container);
  }

  unlockSidebarHeight();

  // Re-apply custom in-stock toggle after morph replaces DOM
  restoreInStockToggle();

}

/**
 * Check whether a Section Rendering API HTML response contains zero products.
 * Parses the HTML string and looks for the empty-state-message or a "0 items" count.
 * @param {string} html - Raw section HTML from the Section Rendering API
 * @returns {boolean}
 */
function hasZeroResultsInHTML(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  if (doc.querySelector('.empty-state-message')) return true;

  const countSpans = doc.querySelectorAll('.products-count-wrapper span');
  for (const span of countSpans) {
    const text = (span.textContent || '').trim();
    const match = text.match(/^(\d+)/);
    if (match && parseInt(match[1], 10) === 0) return true;
  }

  return false;
}

/**
 * Show a non-blocking toast when a filter selection would yield zero results.
 * Auto-dismisses after 3 seconds.
 * @param {string} message
 */
function showFilterToast(message) {
  const existing = document.getElementById('facets-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.id = 'facets-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.textContent = message;
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '1.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#1f2937',
    color: '#fff',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    zIndex: '9999',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    opacity: '0',
    transition: 'opacity 0.3s ease',
  });
  document.body.appendChild(toast);

  requestAnimationFrame(() => { toast.style.opacity = '1'; });

  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

// ─── In Stock (4+) custom toggle filter ─────────────────────
const IN_STOCK_STORAGE_KEY = 'facets:inStockToggle';
const IN_STOCK_THRESHOLD = 1;

/**
 * Apply the In Stock filter: hide product cards with inventory <= threshold.
 * Reads checked state from any `input[name="custom-in-stock"]` checkbox.
 */
function applyInStockFilter() {
  const checkboxes = document.querySelectorAll('input[name="custom-in-stock"]');
  const isChecked = Array.from(checkboxes).some((cb) => cb.checked);

  const cards = document.querySelectorAll('product-card[data-inventory-quantity]');
  cards.forEach((card) => {
    const qty = parseInt(card.getAttribute('data-inventory-quantity'), 10) || 0;
    const gridItem = card.closest('li.product-grid__item');
    const target = gridItem || card;
    if (isChecked && qty <= IN_STOCK_THRESHOLD) {
      target.style.display = 'none';
    } else {
      target.style.display = '';
    }
  });

  try {
    sessionStorage.setItem(IN_STOCK_STORAGE_KEY, isChecked ? '1' : '0');
  } catch (e) {
    // sessionStorage unavailable
  }
}

// Expose for pagination hooks
window.applyInStockFilter = applyInStockFilter;

/**
 * Sync all in-stock checkboxes (desktop + drawer) to the same state and apply.
 * @param {boolean} checked
 */
function syncInStockCheckboxes(checked) {
  const checkboxes = document.querySelectorAll('input[name="custom-in-stock"]');
  checkboxes.forEach((cb) => {
    cb.checked = checked;
  });
  applyInStockFilter();
}

/**
 * Restore in-stock toggle state from sessionStorage (after morph or page load).
 */
function restoreInStockToggle() {
  try {
    const stored = sessionStorage.getItem(IN_STOCK_STORAGE_KEY);
    if (stored === '1') {
      syncInStockCheckboxes(true);
    } else {
      syncInStockCheckboxes(false);
    }
  } catch (e) {
    // sessionStorage unavailable
  }
}

// Delegated change listener — works even after DOM morphs replace checkbox elements
document.addEventListener('change', (event) => {
  const input = event.target;
  if (input && input.name === 'custom-in-stock') {
    syncInStockCheckboxes(input.checked);
  }
});

function navigateToParams(params) {
  const url = new URL(window.location.pathname, window.location.origin);
  url.search = '';
  for (const [param, value] of params.entries()) {
    url.searchParams.append(param, value);
  }
  try {
    sessionStorage.setItem(FACET_SCROLL_FLAG, '1');
  } catch (error) {
    console.debug('Facets: unable to set scroll flag', error);
  }
  window.location.assign(url.toString());
}

document.addEventListener('DOMContentLoaded', () => {
  try {
    if (sessionStorage.getItem(FACET_SCROLL_FLAG) === '1') {
      sessionStorage.removeItem(FACET_SCROLL_FLAG);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  } catch (error) {
    console.debug('Facets: unable to read scroll flag', error);
  }

  // Capture initial filter availability for all containers
  for (const container of getAllFacetsContainers()) {
    captureAvailableOptions(container);
  }

  // Apply custom in-stock toggle on initial load
  restoreInStockToggle();
});

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Strip custom in-stock toggle — client-side only, not a Shopify filter
    newParameters.delete('custom-in-stock');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * If any non-OR filter param changed relative to the current URL, uncheck
   * all OR-style filter checkboxes in the form so stale colour/material
   * selections don't persist when the user switches brand/vendor/type.
   */
  #clearORFiltersIfNonORChanged() {
    const currentParams = new URLSearchParams(window.location.search);
    const formParams = this.createURLParameters();

    // Build a stringified snapshot per non-OR filter param in both sets
    /** @param {URLSearchParams} p */
    const nonORSnapshot = (p) => {
      /** @type {Map<string, string[]>} */
      const map = new Map();
      for (const [key, value] of p.entries()) {
        // Convert dashed param to dot-separated for isORStyleFilter check
        const dashed = key.replace(/\./g, '-');
        if (isORStyleFilter(dashed)) continue;
        if (!map.has(key)) map.set(key, []);
        map.get(key)?.push(value);
      }
      // Sort values for stable comparison
      for (const [, values] of map) values.sort();
      return JSON.stringify([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
    };

    if (nonORSnapshot(currentParams) === nonORSnapshot(formParams)) return;

    // A non-OR filter changed — uncheck all OR-style checkboxes in the form
    this.refs.facetsForm?.querySelectorAll('input[type="checkbox"]').forEach(
      /** @param {HTMLInputElement} input */ (input) => {
        const dashed = input.name.replace(/\./g, '-');
        if (isORStyleFilter(dashed) && input.checked) {
          input.checked = false;
        }
      }
    );
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section.
   * Uses a preflight fetch to check for zero results BEFORE touching the URL or DOM.
   *
   * Cross-filter dependency: if any non-OR filter (brand/vendor/type) changed
   * compared to the current URL, all OR-style filter selections are cleared
   * automatically. This prevents stale colour/material selections from
   * persisting when the user switches brand.
   */
  updateFilters = async () => {
    // Global busy lock — if an update is already in flight, block.
    // Exception: reason 'swatch' means the capture-phase click handler just set
    // the lock for us and this is the initiating call — promote to 'update' and proceed.
    if (facetsBusy && facetsBusyReason !== 'swatch') return;
    setFacetsBusy(true, 'update');

    this.#clearORFiltersIfNonORChanged();
    const params = this.createURLParameters();

    if (!DISABLE_SECTION_RENDER) {
      // Build the candidate URL (same shape #updateURLHash would produce)
      const targetUrl = new URL(window.location.pathname, window.location.origin);
      for (const [key, value] of params.entries()) {
        targetUrl.searchParams.append(key, value);
      }

      lockSidebarHeight();

      try {
        // Preflight: fetch section HTML without changing URL or DOM
        const html = await sectionRenderer.getSectionHTML(this.sectionId, false, targetUrl);

        if (hasZeroResultsInHTML(html)) {
          showFilterToast('No results for that combination.');

          // Revert checkbox state to match the current (unchanged) URL
          const currentParams = new URLSearchParams(window.location.search);
          this.refs.facetsForm?.querySelectorAll('input[type="checkbox"]').forEach(
            /** @param {HTMLInputElement} input */ (input) => {
              const shouldBeChecked = currentParams.getAll(input.name).includes(input.value);
              if (input.checked !== shouldBeChecked) input.checked = shouldBeChecked;
            }
          );

          unlockSidebarHeight();
          setFacetsBusy(false);
          return; // Abort — leave current URL and DOM untouched
        }
      } catch (error) {
        console.debug('[Facets] Preflight failed, proceeding with normal apply:', error);
      }

      // Preflight passed (or was skipped on error) — commit the filter change
      this.#updateURLHash();
      this.dispatchEvent(new FilterUpdateEvent(params));

      try {
        // Use cache: true so renderSection picks up the preflight-cached HTML
        await this.#updateSection(new URL(window.location.href), true);
        await handlePostMorph(this.sectionId);
      } catch (error) {
        console.error('[Facets] Section render failed, falling back to page navigation:', error);
        navigateToParams(params);
      } finally {
        setFacetsBusy(false);
      }
    } else {
      this.#updateURLHash();
      this.dispatchEvent(new FilterUpdateEvent(params));
      setFacetsBusy(false);
      navigateToParams(params);
    }
  };

  /**
   * Updates the section by fetching HTML and morphing the DOM.
   * @param {URL} url - The URL to fetch the section with (including filter params)
   * @param {boolean} [useCache=false] - Whether to use cached section HTML
   * @returns {Promise<void>}
   */
  async #updateSection(url, useCache = false) {
    const renderOptions = { cache: useCache, url: url };
    await sectionRenderer.renderSection(this.sectionId, renderOptions);
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    if (facetsBusy) return;
    setFacetsBusy(true, 'update');

    history.pushState('', '', url);
    const params = this.createURLParameters();
    this.dispatchEvent(new FilterUpdateEvent(params));
    if (!DISABLE_SECTION_RENDER) {
      lockSidebarHeight();
      const targetUrl = new URL(url, window.location.origin);
      this.#updateSection(targetUrl).then(() => {
        return handlePostMorph(this.sectionId);
      }).catch(error => {
        console.error('[Facets] Section render failed, falling back to page navigation:', error);
        window.location.assign(url);
      }).finally(() => {
        setFacetsBusy(false);
      });
    } else {
      setFacetsBusy(false);
      window.location.assign(url);
    }
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form.
   * For OR-style filters, enforces single-select before toggling.
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      // Apply single-select for OR-style filters (uncheck siblings across containers)
      // applySingleSelectForOR returns false if the input is already checked (deselect),
      // in which case we just toggle normally.
      applySingleSelectForOR(closestInput);

      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    if (!DISABLE_SECTION_RENDER) {
      sectionRenderer.getSectionHTML(this.sectionId, true, url);
    }
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    this.#syncInputAttributes();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Sync hidden input attribute values with their current values so FormData picks up both ends
   */
  #syncInputAttributes() {
    const { minInput, maxInput } = this.refs;

    if (minInput) minInput.setAttribute('value', minInput.value);
    if (maxInput) maxInput.setAttribute('value', maxInput.value);
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    // navigateToParams removed - updateFilters() handles AJAX section rendering
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
    // window.location.assign removed - updateFiltersByURL() handles AJAX section rendering
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    // navigateToParams removed - updateFilters() handles AJAX section rendering
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};


document.addEventListener('DOMContentLoaded', function() {
  initializeAllSliders();
  // Retry after delays to catch late-initializing components (accordion, noUiSlider CDN)
  setTimeout(() => initializeAllSliders(), 500);
  setTimeout(() => initializeAllSliders(), 1500);
});

// Listen for Horizon's FilterUpdate event
document.addEventListener(ThemeEvents.FilterUpdate, function(event) {
  // Multiple timeouts to catch different morphing speeds
  setTimeout(() => forceReinitializeSliders(), 200);
  setTimeout(() => forceReinitializeSliders(), 600);
  setTimeout(() => forceReinitializeSliders(), 1000);

  // Re-initialize wishlist buttons after products are loaded
  setTimeout(() => {
    if (typeof window.wishlistButtonsInit === 'function') {
      window.wishlistButtonsInit();
    }
  }, 500);
});

// Also listen for any form submissions in the facets
document.addEventListener('submit', function(e) {
  if (e.target.closest('.facets')) {
    setTimeout(() => forceReinitializeSliders(), 500);
  }
});

// Listen for any checkbox changes in facets
document.addEventListener('change', function(e) {
  if (e.target.closest('.facets') && e.target.type === 'checkbox') {
    setTimeout(() => forceReinitializeSliders(), 300);
  }
});

// Listen for mobile drawer filter changes
document.addEventListener('change', function(e) {
  // Check if the change is from a mobile drawer filter
  if (e.target.closest('.facets.facets--drawer') && e.target.type === 'checkbox') {
    setTimeout(() => forceReinitializeSliders(), 300);
  }
});

// Listen for mobile drawer form submissions
document.addEventListener('submit', function(e) {
  if (e.target.closest('.facets.facets--drawer')) {
    setTimeout(() => forceReinitializeSliders(), 500);
  }
});

// Simple observer to detect when slider containers are removed
const sliderObserver = new MutationObserver(function(mutations) {
  let slidersRemoved = false;
  
  mutations.forEach(function(mutation) {
    if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
      Array.from(mutation.removedNodes).forEach(node => {
        if (node.nodeType === 1 && /** @type {Element} */ (node).classList?.contains('slider-container')) {
          slidersRemoved = true;
        }
      });
    }
    
    // Also detect when mobile drawer content changes
    if (mutation.type === 'childList' && mutation.target.classList && mutation.target.classList.contains('facets--drawer')) {
      slidersRemoved = true;
    }
  });
  
  if (slidersRemoved) {
    // Immediate recreation when sliders are removed
    forceReinitializeSliders();
  }
});

// Start observing after page loads
document.addEventListener('DOMContentLoaded', function() {
  setTimeout(() => {
    sliderObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }, 1000);
});

// Debounce slider initialization to prevent spam
let initializationTimeout;
function debouncedInitializeAllSliders(clearCache = false) {
  clearTimeout(initializationTimeout);
  initializationTimeout = setTimeout(() => {
    if (clearCache) {
      // Clear caches to force recreation
      sliderCache.clear();
      numericFilterCache.clear();
    }
    initializeAllSliders();
  }, 100); // Reduced delay
}

// Clear cache and reinitialize when filters change
function forceReinitializeSliders() {
  debouncedInitializeAllSliders(true);
}

// DISABLED: MutationObserver was causing infinite loops
// Only rely on FilterUpdate event for now

// Cache for performance optimization
const sliderCache = new Map();
const numericFilterCache = new Map();

function initializeAllSliders() {
  // Desktop filters - inside .facets.facets--vertical
  let desktopNumericFilters = document.querySelectorAll('.facets.facets--vertical [param-name*="filter.p.m."]:not([param-name*="filter.p.m.availability"]):not([param-name*="filter.p.m.product_type"]):not([param-name*="filter.p.m.vendor"])');
  
  if (desktopNumericFilters.length === 0) {
    // Try alternative patterns for desktop
    desktopNumericFilters = document.querySelectorAll('.facets.facets--vertical [param-name*="filter-p-m-"]:not([param-name*="filter-p-m-availability"]):not([param-name*="filter-p-m-product_type"]):not([param-name*="filter-p-m-vendor"])');
  }
  
  if (desktopNumericFilters.length === 0) {
    // Fallback to ID-based selection for desktop
    desktopNumericFilters = document.querySelectorAll('.facets.facets--vertical [id*="filter-p-m-"]:not([id*="filter-p-m-availability"]):not([id*="filter-p-m-product_type"]):not([id*="filter-p-m-vendor"])');
  }
  
  // Mobile filters - inside .facets.facets--drawer
  let mobileNumericFilters = document.querySelectorAll('.facets.facets--drawer [param-name*="filter.p.m."]:not([param-name*="filter.p.m.availability"]):not([param-name*="filter.p.m.product_type"]):not([param-name*="filter.p.m.vendor"])');
  
  if (mobileNumericFilters.length === 0) {
    // Try alternative patterns for mobile
    mobileNumericFilters = document.querySelectorAll('.facets.facets--drawer [param-name*="filter-p-m-"]:not([param-name*="filter-p-m-availability"]):not([param-name*="filter-p-m-product_type"]):not([param-name*="filter-p-m-vendor"])');
  }
  
  if (mobileNumericFilters.length === 0) {
    // Fallback to ID-based selection for mobile
    mobileNumericFilters = document.querySelectorAll('.facets.facets--drawer [id*="filter-p-m-"]:not([id*="filter-p-m-availability"]):not([id*="filter-p-m-product_type"]):not([id*="filter-p-m-vendor"])');
  }
  
  // Combine desktop and mobile filters
  const allNumericFilters = [...desktopNumericFilters, ...mobileNumericFilters];
  
  // Find price filters (both desktop and mobile)
  const desktopPriceFilters = document.querySelectorAll('.facets.facets--vertical price-facet-component');
  const mobilePriceFilters = document.querySelectorAll('.facets.facets--drawer price-facet-component');
  const allPriceFilters = [...desktopPriceFilters, ...mobilePriceFilters];
  
  // Batch process filters for better performance
  const filtersToProcess = [];
  
  // Process numeric metafield filters (both desktop and mobile)
  allNumericFilters.forEach(filterList => {
    const paramName = filterList.getAttribute('param-name') || filterList.id;

    // Skip text-based filters that should never become sliders
    if (isSliderExcluded(paramName)) return;

    // Check cache first
    if (numericFilterCache.has(paramName)) {
      if (numericFilterCache.get(paramName)) {
        filtersToProcess.push(filterList);
      }
    } else {
      // Check if numeric and cache result
      const isNumeric = isNumericFilter(filterList);
      numericFilterCache.set(paramName, isNumeric);
      if (isNumeric) {
        filtersToProcess.push(filterList);
      }
    }
  });
  
  // Process price filters (both desktop and mobile)
  allPriceFilters.forEach(priceFilter => {
    initializePriceSliderIfNeeded(priceFilter);
  });
  
  // Process in batches to avoid blocking UI
  processSlidersInBatches(filtersToProcess);
}

// Process sliders in small batches to prevent UI blocking
function processSlidersInBatches(filters, batchSize = 3) {
  if (filters.length === 0) return;
  
  const batch = filters.splice(0, batchSize);
  batch.forEach(filterList => {
    initializeNumericSliderIfNeeded(filterList);
  });
  
  // Process next batch after a small delay
  if (filters.length > 0) {
    setTimeout(() => processSlidersInBatches(filters, batchSize), 10);
  }
}

// Check if a filter contains numeric values
function isNumericFilter(filterList) {
  const checkboxes = filterList.querySelectorAll('input[type="checkbox"]');
  if (checkboxes.length === 0) return false;
  
  // Check if at least 70% of values are numeric
  let numericCount = 0;
  let totalCount = 0;
  
  checkboxes.forEach(checkbox => {
    const value = getValueFromCheckbox(checkbox);
    if (value !== null) {
      totalCount++;
      if (!isNaN(parseFloat(value)) && isFinite(value)) {
        numericCount++;
      }
    }
  });
  
  return totalCount > 0 && (numericCount / totalCount) >= 0.7;
}

// Extract numeric value from checkbox (handles different metafield types)
function getValueFromCheckbox(checkbox) {
  const labelText = getCheckboxLabelText(checkbox);
  const labelNumber = extractNumericFromText(labelText);
  if (labelNumber !== null) {
    return labelNumber;
  }

  const checkboxValue = checkbox.value;
  if (!checkboxValue) {
    return null;
  }

  // Guard against large numeric IDs (e.g., metaobject IDs) masquerading as numeric values.
  const digitsOnly = checkboxValue.replace(/[^\d]/g, '');
  const labelHasDigits = /\d/.test(labelText || '');
  if (!labelHasDigits && digitsOnly.length >= 9) {
    return null;
  }

  return extractNumericFromText(checkboxValue);
}

function getCheckboxLabelText(checkbox) {
  if (!checkbox) return '';

  if (checkbox.dataset && checkbox.dataset.label) {
    return checkbox.dataset.label;
  }

  const dataLabel = checkbox.getAttribute('data-label');
  if (dataLabel) {
    return dataLabel;
  }

  const ariaLabel = checkbox.getAttribute('aria-label');
  if (ariaLabel) {
    return ariaLabel;
  }

  const siblingLabel = checkbox.nextElementSibling && checkbox.nextElementSibling.tagName === 'LABEL'
    ? checkbox.nextElementSibling
    : null;
  if (siblingLabel) {
    return siblingLabel.textContent.trim();
  }

  if (checkbox.id) {
    const forLabel = checkbox.ownerDocument && checkbox.ownerDocument.querySelector(`label[for="${checkbox.id}"]`);
    if (forLabel) {
      return forLabel.textContent.trim();
    }
  }

  const fallbackLabel = checkbox.closest('label');
  if (fallbackLabel) {
    return fallbackLabel.textContent.trim();
  }

  return '';
}

function extractNumericFromText(text) {
  if (!text) return null;
  const match = text.match(/-?[\d.,]+/);
  if (!match) return null;
  const cleanNumber = match[0].replace(/,/g, '');
  const parsed = parseFloat(cleanNumber);
  return Number.isFinite(parsed) ? parsed : null;
}

// Get filter metadata from param-name, data-filter-param-name, or ID
function getFilterMetadata(filterList) {
  let paramName = filterList.getAttribute('param-name');
  let metafieldPart;
  
  // Check for mobile drawer structure
  if (!paramName) {
    const drawerParent = filterList.closest('.facets.facets--drawer');
    if (drawerParent) {
      const accordionParent = filterList.closest('accordion-custom');
      if (accordionParent) {
        paramName = accordionParent.getAttribute('data-filter-param-name');
      }
    }
  }
  
  if (paramName) {
    // Extract metafield name from param-name
    // Example: "filter.p.m.product_specifications-flow_rate_L_per_min" or "filter-p-m-product_specifications-flow_rate_L_per_min"
    if (paramName.includes('filter.p.m.')) {
      metafieldPart = paramName.replace('filter.p.m.', '').replace(/\./g, '_');
    } else if (paramName.includes('filter-p-m-')) {
      metafieldPart = paramName.replace('filter-p-m-', '');
    } else {
      metafieldPart = paramName;
    }
  } else {
    // Fallback to ID extraction
    const id = filterList.id;
    const parts = id.split('-');
    metafieldPart = parts[parts.length - 1];
  }
  
  // Create readable label
  const label = metafieldPart
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, l => l.toUpperCase());
  
  // Detect unit from common patterns
  let unit = '';
  if (metafieldPart.includes('rate') || metafieldPart.includes('flow')) unit = 'L/min';
  else if (metafieldPart.includes('weight') || metafieldPart.includes('kg')) unit = 'kg';
  else if (metafieldPart.includes('price') || metafieldPart.includes('cost')) unit = '$';
  else if (metafieldPart.includes('length') || metafieldPart.includes('width') || metafieldPart.includes('height')) unit = 'cm';
  else if (metafieldPart.includes('temp')) unit = '°C';
  else if (metafieldPart.includes('percent')) unit = '%';
  
  return { label, unit, id: metafieldPart };
}

// Price slider initialization
function initializePriceSliderIfNeeded(priceFilter) {
  const minInput = priceFilter.querySelector('input[ref="minInput"]');
  const maxInput = priceFilter.querySelector('input[ref="maxInput"]');
  
  if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;

  const parseMoneyValue = (rawValue, fallback) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') return fallback;
    // Remove thousands separators (commas) and spaces, keep only digits and decimal point
    const cleaned = String(rawValue).replace(/,/g, '').replace(/\s/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  
  const rawMinValue = parseMoneyValue(minInput.getAttribute('data-min'), 0);
  const rawMaxValue = parseMoneyValue(maxInput.getAttribute('data-max'), 1000);
  const sliderStep = 1;
  const sliderMin = Math.floor(rawMinValue / sliderStep) * sliderStep;
  const sliderMax = Math.max(sliderMin, Math.ceil(rawMaxValue / sliderStep) * sliderStep);
  const alignToStep = (value) => Math.round(value / sliderStep) * sliderStep;
  const clampToSliderRange = (value) => Math.max(sliderMin, Math.min(sliderMax, value));

  let currentMin = clampToSliderRange(alignToStep(parseMoneyValue(minInput.value, sliderMin)));
  let currentMax = clampToSliderRange(alignToStep(parseMoneyValue(maxInput.value, sliderMax)));

  if (currentMin > currentMax) {
    [currentMin, currentMax] = [currentMax, currentMin];
  }

  const currentMinDisplay = Math.round(currentMin).toString();
  const currentMaxDisplay = Math.round(currentMax).toString();

  // Ensure the hidden inputs have their value attributes in sync with the live values
  const currentMinString = Math.round(currentMin).toString();
  const currentMaxString = Math.round(currentMax).toString();
  minInput.value = currentMinString;
  maxInput.value = currentMaxString;
  minInput.setAttribute('value', currentMinString);
  maxInput.setAttribute('value', currentMaxString);
  minInput.setAttribute('data-min', Math.round(sliderMin).toString());
  minInput.setAttribute('data-max', Math.round(sliderMax).toString());
  maxInput.setAttribute('data-min', Math.round(sliderMin).toString());
  maxInput.setAttribute('data-max', Math.round(sliderMax).toString());
  
  const sectionId = priceFilter.closest('.shopify-section')?.id || 'price-filter';
  const inDrawer = !!priceFilter.closest('.facets--drawer');
  const contextId = `${sectionId}-${inDrawer ? 'drawer' : 'default'}`;
  const sliderId = `price-range-slider-${contextId}`;
  const containerClass = `price-slider-container-${contextId}`;
  const containerRoot = priceFilter.closest('details') || priceFilter;
  
  // Check if slider already exists
  const existingSlider = containerRoot.querySelector(`#${sliderId}`);
  if (existingSlider && existingSlider.noUiSlider) {
    return;
  }
  
  // Remove any existing containers
  const existingContainers = containerRoot.querySelectorAll(`.${containerClass}`);
  existingContainers.forEach(container => container.remove());

  // Create slider container
  const sliderContainer = document.createElement('div');
  sliderContainer.className = `${containerClass} loading`;
  sliderContainer.innerHTML = `
    <div class="range-controls">
      <div class="range-inputs">
        <div class="range-input-group">
          <label for="${sliderId}-min-input">Min:</label>
          <input type="number" id="${sliderId}-min-input" value="${currentMinDisplay}" min="${sliderMin}" max="${sliderMax}" step="${sliderStep}">
        </div>
        <div class="range-input-group">
          <label for="${sliderId}-max-input">Max:</label>
          <input type="number" id="${sliderId}-max-input" value="${currentMaxDisplay}" min="${sliderMin}" max="${sliderMax}" step="${sliderStep}">
        </div>
      </div>
      <div id="${sliderId}" class="range-slider"></div>
      <div class="range-display">
        <div id="${sliderId}-display"><span>$${currentMinDisplay}</span> <span>$${currentMaxDisplay}</span></div>
      </div>
    </div>
  `;

  // Find the correct insertion point
  const detailsElement = priceFilter.closest('details');
  let insertionPoint = null;
  let insertBeforeElement = null;

  if (detailsElement) {
    // Find the facets__inputs div (the content area after summary)
    const facetsInputsDiv = detailsElement.querySelector('.facets__inputs');
    if (facetsInputsDiv) {
      insertionPoint = facetsInputsDiv;
      insertBeforeElement = facetsInputsDiv.firstChild;
    } else {
      // Fallback: find the first element after summary
      const summary = detailsElement.querySelector('summary');
      if (summary) {
        // Find the next element sibling (not text node)
        let nextElement = summary.nextElementSibling;
        if (nextElement) {
          insertionPoint = detailsElement;
          insertBeforeElement = nextElement;
        }
      }
    }
  }

  if (insertionPoint && insertBeforeElement) {
    insertionPoint.insertBefore(sliderContainer, insertBeforeElement);
  } else if (insertionPoint) {
    insertionPoint.appendChild(sliderContainer);
  } else {
    // Last resort: append to the price filter
    priceFilter.appendChild(sliderContainer);
  }

  // Initialize slider — only hide original inputs on success; fallback to inputs if slider fails
  const sliderCreated = initializePriceSlider(sliderId, sliderMin, sliderMax, currentMin, currentMax, minInput, maxInput, sliderStep);

  if (sliderCreated) {
    minInput.style.display = 'none';
    maxInput.style.display = 'none';
    // Remove loading state
    setTimeout(() => {
      sliderContainer.classList.remove('loading');
    }, 50);
  } else {
    // Slider failed to create (noUiSlider not loaded yet) — remove container, keep original inputs
    sliderContainer.remove();
    minInput.style.display = '';
    maxInput.style.display = '';
  }
}

// Universal numeric slider initialization with caching
function initializeNumericSliderIfNeeded(filterList) {
  const paramName = filterList.getAttribute('param-name') || filterList.id;
  const metadata = getFilterMetadata(filterList);
  const isMobile = filterList.closest('.facets.facets--drawer');
  
  // Make slider IDs unique for mobile vs desktop
  const sliderId = isMobile ? `${metadata.id}-mobile-slider` : `${metadata.id}-slider`;
  const containerClass = isMobile ? `${metadata.id}-mobile-slider-container` : `${metadata.id}-slider-container`;
  
  // Debug for mobile filters
  if (isMobile) {
    // console.log(`Creating slider for mobile filter: ${metadata.id}`);
  }
  
  // Check if slider already exists and is valid
  const existingSlider = document.getElementById(sliderId);
  if (existingSlider && existingSlider.noUiSlider) {
    if (isMobile) {
      // console.log(`Mobile slider already exists for: ${metadata.id}`);
    }
    return;
  }
  
  // Remove any existing containers
  const existingContainers = document.querySelectorAll(`.${containerClass}`);
  existingContainers.forEach(container => container.remove());

  // Hide the checkbox list only when we're actually creating a slider
  filterList.classList.add('converted-to-slider');

  // Use cached values if available
  let sliderData = sliderCache.get(paramName);
  
  if (!sliderData) {
    // Extract and cache values from checkboxes
    const checkboxes = filterList.querySelectorAll('input[type="checkbox"]');
    const numericValues = Array.from(checkboxes)
      .map(cb => getValueFromCheckbox(cb))
      .filter(val => val !== null && !isNaN(val))
      .sort((a, b) => a - b);

    if (numericValues.length === 0) return;

    const minValue = Math.min(...numericValues);
    const maxValue = Math.max(...numericValues);
    const range = maxValue - minValue;
    const hasDecimals = numericValues.some(val => val % 1 !== 0);
    let step = hasDecimals ? 0.1 : 1;
    
    if (range > 100) step = hasDecimals ? 1 : 10;
    else if (range > 10) step = hasDecimals ? 0.5 : 1;

    // Cache the data
    sliderData = { checkboxes, numericValues, minValue, maxValue, step };
    sliderCache.set(paramName, sliderData);
  }

  // Get currently selected values
  const checkedValues = Array.from(sliderData.checkboxes)
    .filter(cb => cb.checked)
    .map(cb => getValueFromCheckbox(cb))
    .filter(val => val !== null && !isNaN(val))
    .sort((a, b) => a - b);

  let startMin = sliderData.minValue;
  let startMax = sliderData.maxValue;
  
  if (checkedValues.length > 0) {
    startMin = Math.min(...checkedValues);
    startMax = Math.max(...checkedValues);
  }

  // Create slider container with loading state
  const sliderContainer = document.createElement('div');
  sliderContainer.className = `${containerClass} loading`;
  sliderContainer.innerHTML = `
    <div class="range-controls">
      <div class="range-inputs">
        <div class="range-input-group">
          <label for="${sliderId}-min-input">Min:</label>
          <input type="number" id="${sliderId}-min-input" value="${startMin}" min="${sliderData.minValue}" max="${sliderData.maxValue}" step="${sliderData.stepValue}">
        </div>
        <div class="range-input-group">
          <label for="${sliderId}-max-input">Max:</label>
          <input type="number" id="${sliderId}-max-input" value="${startMax}" min="${sliderData.minValue}" max="${sliderData.maxValue}" step="${sliderData.stepValue}">
        </div>
      </div>
      <div id="${sliderId}" class="range-slider"></div>
      <div class="range-display">
        <div id="${sliderId}-display"><span>${formatValue(startMin, metadata.unit)}</span> <span>${formatValue(startMax, metadata.unit)}</span></div>
      </div>
    </div>
  `;

  // Find the details element that contains this filter list
  const detailsElement = filterList.closest('details');
  
  if (detailsElement) {
    // Find the facets__inputs div (the content area after summary)
    const facetsInputsDiv = detailsElement.querySelector('.facets__inputs');
    
    if (facetsInputsDiv) {
      // Insert slider at the beginning of the facets__inputs div
      facetsInputsDiv.insertBefore(sliderContainer, facetsInputsDiv.firstChild);
    } else {
      // Fallback: insert after summary if facets__inputs not found
      const summary = detailsElement.querySelector('summary');
      if (summary && summary.nextSibling) {
        detailsElement.insertBefore(sliderContainer, summary.nextSibling);
      } else {
        // Last resort: insert before the hidden list
        filterList.parentNode.insertBefore(sliderContainer, filterList);
      }
    }
  } else {
    // Fallback: insert before the hidden list if no details found
    filterList.parentNode.insertBefore(sliderContainer, filterList);
  }

  // Initialize slider with cached values
  initializeNumericSlider(sliderId, sliderData.minValue, sliderData.maxValue, sliderData.numericValues, sliderData.checkboxes, startMin, startMax, sliderData.step, metadata);
  
  // Remove loading state after slider is created
  setTimeout(() => {
    sliderContainer.classList.remove('loading');
  }, 50);
}

// Update existing slider without recreating
function updateExistingSlider(filterList, sliderElement, metadata) {
  const checkboxes = filterList.querySelectorAll('input[type="checkbox"]');
  const checkedValues = Array.from(checkboxes)
    .filter(cb => cb.checked)
    .map(cb => getValueFromCheckbox(cb))
    .filter(val => val !== null && !isNaN(val))
    .sort((a, b) => a - b);

  if (checkedValues.length > 0) {
    const startMin = Math.min(...checkedValues);
    const startMax = Math.max(...checkedValues);
    
    // Update slider position without triggering events
    sliderElement.noUiSlider.set([startMin, startMax], false);
  }
}

// Format value with unit
function formatValue(value, unit) {
  const formatted = value % 1 === 0 ? value.toString() : value.toFixed(1);
  return unit ? `${formatted} ${unit}` : formatted;
}

function initializeFlowRateSliderIfNeeded(filterList) {
  // Remove existing slider if it exists (force recreation)
  const existingSlider = filterList.parentNode.querySelector('.flow-rate-slider-container');
  if (existingSlider) {
    existingSlider.remove();
  }

  // Checkboxes are hidden via CSS, no need to hide manually
  
  // Extract values from checkboxes
  const checkboxes = filterList.querySelectorAll('input[type="checkbox"]');
  const flowRates = Array.from(checkboxes).map(cb => {
    // Extract number from checkbox value/label
    const value = cb.value || cb.nextElementSibling?.textContent || '';
    return parseFloat(value.match(/[\d.]+/)?.[0]) || 0;
  }).filter(rate => rate > 0).sort((a, b) => a - b);

  if (flowRates.length === 0) return;

  const minRate = Math.min(...flowRates);
  const maxRate = Math.max(...flowRates);

  // Get currently selected values from checked checkboxes
  const checkedRates = Array.from(checkboxes)
    .filter(cb => cb.checked)
    .map(cb => {
      const value = cb.value || cb.nextElementSibling?.textContent || '';
      return parseFloat(value.match(/[\d.]+/)?.[0]) || 0;
    })
    .filter(rate => rate > 0)
    .sort((a, b) => a - b);

  // Set slider start values based on current selection
  let startMin = minRate;
  let startMax = maxRate;
  
  if (checkedRates.length > 0) {
    startMin = Math.min(...checkedRates);
    startMax = Math.max(...checkedRates);
  }

  // Create slider container with loading state
  const sliderContainer = document.createElement('div');
  sliderContainer.className = 'flow-rate-slider-container loading';
  sliderContainer.innerHTML = `
    <div class="filter-title">Flow Rate (L/min)</div>
    <div id="flow-rate-slider" class="range-slider"></div>
    <div class="range-display">
      <div id="flow-rate-display"><span>${startMin}</span> <span>${startMax}</span> L/min</div>
    </div>
  `;

  // Find the details element that contains this filter list
  const detailsElement = filterList.closest('details');
  
  if (detailsElement) {
    // Find the facets__inputs div (the content area after summary)
    const facetsInputsDiv = detailsElement.querySelector('.facets__inputs');
    
    if (facetsInputsDiv) {
      // Insert slider at the beginning of the facets__inputs div
      facetsInputsDiv.insertBefore(sliderContainer, facetsInputsDiv.firstChild);
    } else {
      // Fallback: insert after summary if facets__inputs not found
      const summary = detailsElement.querySelector('summary');
      if (summary && summary.nextSibling) {
        detailsElement.insertBefore(sliderContainer, summary.nextSibling);
      } else {
        // Last resort: insert before the hidden list
  filterList.parentNode.insertBefore(sliderContainer, filterList);
      }
    }
  } else {
    // Fallback: insert before the hidden list if no details found
    filterList.parentNode.insertBefore(sliderContainer, filterList);
  }

  // Initialize slider with current values
  initializeFlowRateSlider(minRate, maxRate, flowRates, checkboxes, startMin, startMax);
  
  // Remove loading state after slider is created
  setTimeout(() => {
    sliderContainer.classList.remove('loading');
  }, 50);
}

// Universal numeric slider initialization
function initializeNumericSlider(sliderId, min, max, availableValues, checkboxes, startMin = min, startMax = max, step = 0.1, metadata) {
  const slider = document.getElementById(sliderId);
  const displayId = `${sliderId}-display`;
  
  // Destroy existing slider if it exists
  if (slider && slider.noUiSlider) {
    slider.noUiSlider.destroy();
  }
  
  // Create slider
  noUiSlider.create(slider, {
    start: [startMin, startMax],
    connect: true,
    range: { 'min': min, 'max': max },
    step: step,
    format: {
      to: value => step >= 1 ? Math.round(value) : parseFloat(value).toFixed(1),
      from: value => parseFloat(value)
    }
  });

  // Handle slider changes - update display and inputs in real-time
  slider.noUiSlider.on('update', function(values) {
    const [minVal, maxVal] = values.map(v => parseFloat(v));
    
    // Update display (no form submission)
    const displayElement = document.getElementById(displayId);
    if (displayElement) {
      displayElement.innerHTML = `<span>${formatValue(minVal, metadata.unit)}</span> <span>${formatValue(maxVal, metadata.unit)}</span>`;
    }
    
    // Update input fields (no form submission)
    updateInputFields(sliderId, minVal, maxVal);
  });

  // Handle slider changes - filter products only when user stops dragging
  slider.noUiSlider.on('set', function(values) {
    const [minVal, maxVal] = values.map(v => parseFloat(v));
    
    // Update input fields
    updateInputFields(sliderId, minVal, maxVal);
    
    // Check/uncheck original checkboxes based on range
    checkboxes.forEach(checkbox => {
      const value = getValueFromCheckbox(checkbox);
      if (value !== null) {
        const inRange = value >= minVal && value <= maxVal;
        
        if (checkbox.checked !== inRange) {
          checkbox.checked = inRange;
          // Trigger change event to update Shopify filters
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  });

  // Handle input field changes
  setupInputFieldListeners(sliderId, slider, min, max, step, checkboxes, metadata);
}

// Update input fields when slider changes
function updateInputFields(sliderId, minVal, maxVal) {
  const minInput = document.getElementById(`${sliderId}-min-input`);
  const maxInput = document.getElementById(`${sliderId}-max-input`);
  
  if (minInput) minInput.value = minVal;
  if (maxInput) maxInput.value = maxVal;
}

// Setup event listeners for input fields
function setupInputFieldListeners(sliderId, slider, min, max, step, checkboxes, metadata) {
  const minInput = document.getElementById(`${sliderId}-min-input`);
  const maxInput = document.getElementById(`${sliderId}-max-input`);
  const displayId = `${sliderId}-display`;
  
  if (!minInput || !maxInput) return;
  
  // Function to validate and update slider from inputs
  function updateSliderFromInputs() {
    let minVal = parseFloat(minInput.value);
    let maxVal = parseFloat(maxInput.value);
    
    // Validate values
    if (isNaN(minVal)) minVal = min;
    if (isNaN(maxVal)) maxVal = max;
    
    // Ensure min <= max
    if (minVal > maxVal) {
      [minVal, maxVal] = [maxVal, minVal];
      minInput.value = minVal;
      maxInput.value = maxVal;
    }
    
    // Clamp to bounds
    minVal = Math.max(min, Math.min(max, minVal));
    maxVal = Math.max(min, Math.min(max, maxVal));
    
    // Update input values if clamped
    minInput.value = minVal;
    maxInput.value = maxVal;
    
    // Update slider
    slider.noUiSlider.set([minVal, maxVal]);
    
    // Update display
    const displayElement = document.getElementById(displayId);
    if (displayElement) {
      displayElement.innerHTML = `<span>${formatValue(minVal, metadata.unit)}</span> <span>${formatValue(maxVal, metadata.unit)}</span>`;
    }
    
    // Update checkboxes
    checkboxes.forEach(checkbox => {
      const value = getValueFromCheckbox(checkbox);
      if (value !== null) {
        const inRange = value >= minVal && value <= maxVal;
        
        if (checkbox.checked !== inRange) {
          checkbox.checked = inRange;
          // Trigger change event to update Shopify filters
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }
  
  // Add event listeners
  minInput.addEventListener('change', updateSliderFromInputs);
  maxInput.addEventListener('blur', updateSliderFromInputs);
  
  // Allow Enter key to trigger update
  minInput.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      updateSliderFromInputs();
    }
  });
  
  maxInput.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      updateSliderFromInputs();
    }
  });
}

function initializePriceSlider(sliderId, min, max, startMin, startMax, minInput, maxInput, step = 0.01) {
  const slider = document.getElementById(sliderId);
  const displayId = `${sliderId}-display`;
  
  // Destroy existing slider if it exists
  if (slider && slider.noUiSlider) {
    slider.noUiSlider.destroy();
  }
  
  // Create slider - guard against noUiSlider not being loaded yet
  if (typeof noUiSlider === 'undefined' || !slider) return false;

  try {
    noUiSlider.create(slider, {
      start: [startMin, startMax],
      connect: true,
      range: { 'min': min, 'max': max },
      step,
      format: {
        to: value => Math.round(parseFloat(value)).toString(),
        from: value => parseFloat(value)
      }
    });
  } catch (e) {
    return false;
  }

  // Handle slider changes - update display and inputs in real-time
  slider.noUiSlider.on('update', function(values) {
    const [minVal, maxVal] = values.map(v => parseFloat(v));

    // Update display with formatted whole numbers
    const displayElement = document.getElementById(displayId);
    if (displayElement) {
      displayElement.innerHTML = `<span>$${Math.round(minVal).toLocaleString('en-AU')}</span> <span>$${Math.round(maxVal).toLocaleString('en-AU')}</span>`;
    }

    // Update input fields
    updatePriceInputFields(sliderId, minVal, maxVal, step);
  });

  // Handle slider changes - update price filter when user stops dragging
  slider.noUiSlider.on('set', function(values) {
    const [minVal, maxVal] = values.map(v => parseFloat(v));
    
    // Update input fields
    updatePriceInputFields(sliderId, minVal, maxVal, step);
    
    // Update the original price inputs and mirror attribute values for FormData compatibility
    const formattedMin = Math.round(minVal).toString();
    const formattedMax = Math.round(maxVal).toString();

    minInput.value = formattedMin;
    maxInput.value = formattedMax;
    minInput.setAttribute('value', formattedMin);
    maxInput.setAttribute('value', formattedMax);
    
    // Trigger the price filter update
    const priceFacet = minInput.closest('price-facet-component');
    if (priceFacet && typeof priceFacet.updatePriceFilterAndResults === 'function') {
      priceFacet.updatePriceFilterAndResults();
    }
  });

  // Setup input field listeners
  setupPriceInputFieldListeners(sliderId, slider, min, max, minInput, maxInput, step);
  return true;
}

// Update price input fields when slider changes
function updatePriceInputFields(sliderId, minVal, maxVal, step = 0.01) {
  const minInput = document.getElementById(`${sliderId}-min-input`);
  const maxInput = document.getElementById(`${sliderId}-max-input`);

  if (minInput) minInput.value = Math.round(minVal).toString();
  if (maxInput) maxInput.value = Math.round(maxVal).toString();
}

// Setup event listeners for price input fields
function setupPriceInputFieldListeners(sliderId, slider, min, max, originalMinInput, originalMaxInput, step = 0.01) {
  const minInput = document.getElementById(`${sliderId}-min-input`);
  const maxInput = document.getElementById(`${sliderId}-max-input`);
  const displayId = `${sliderId}-display`;
  
  if (!minInput || !maxInput) return;
  const INPUT_COMMIT_DELAY_MS = 400;
  const VALUE_EPSILON = 0.009;
  let commitTimeoutId = null;
  let pendingValues = null;
  const alignToStep = (value) => Math.round(value / step) * step;
  let lastCommittedValues = slider.noUiSlider.get().map((value) => alignToStep(Number.parseFloat(value) || 0));

  const updateDisplay = (minVal, maxVal) => {
    const displayElement = document.getElementById(displayId);
    if (displayElement) {
      displayElement.innerHTML = `<span>$${Math.round(minVal).toLocaleString('en-AU')}</span> <span>$${Math.round(maxVal).toLocaleString('en-AU')}</span>`;
    }
  };

  const sanitizeValues = () => {
    let minVal = parseFloat(minInput.value);
    let maxVal = parseFloat(maxInput.value);

    if (isNaN(minVal)) minVal = min;
    if (isNaN(maxVal)) maxVal = max;

    if (minVal > maxVal) {
      [minVal, maxVal] = [maxVal, minVal];
    }

    minVal = Math.max(min, Math.min(max, minVal));
    maxVal = Math.max(min, Math.min(max, maxVal));
    minVal = alignToStep(minVal);
    maxVal = alignToStep(maxVal);

    return { minVal, maxVal };
  };

  const hasValueChanged = (minVal, maxVal) => {
    return Math.abs(minVal - lastCommittedValues[0]) > VALUE_EPSILON || Math.abs(maxVal - lastCommittedValues[1]) > VALUE_EPSILON;
  };

  const previewSliderValues = (minVal, maxVal) => {
    slider.noUiSlider.set([minVal, maxVal], false);
  };

  const clearPendingCommit = () => {
    if (commitTimeoutId) {
      clearTimeout(commitTimeoutId);
      commitTimeoutId = null;
    }
  };

  const commitValues = (minVal, maxVal) => {
    clearPendingCommit();
    pendingValues = null;

    if (!hasValueChanged(minVal, maxVal)) return;

    slider.noUiSlider.set([minVal, maxVal]);
    lastCommittedValues = [minVal, maxVal];
  };

  const scheduleCommit = () => {
    clearPendingCommit();
    commitTimeoutId = window.setTimeout(() => {
      if (!pendingValues) return;
      commitValues(pendingValues.min, pendingValues.max);
    }, INPUT_COMMIT_DELAY_MS);
  };

  const handleInputChange = (forceCommit = false) => {
    const { minVal, maxVal } = sanitizeValues();
    minInput.value = Math.round(minVal).toString();
    maxInput.value = Math.round(maxVal).toString();
    updateDisplay(minVal, maxVal);

    if (!hasValueChanged(minVal, maxVal)) {
      if (forceCommit) {
        commitValues(minVal, maxVal);
      } else {
        clearPendingCommit();
        pendingValues = null;
      }
      return;
    }

    previewSliderValues(minVal, maxVal);
    pendingValues = { min: minVal, max: maxVal };

    if (forceCommit) {
      commitValues(minVal, maxVal);
    } else {
      scheduleCommit();
    }
  };

  const handleImmediateCommit = (event) => {
    if (event?.cancelable) {
      event.preventDefault();
    }
    handleInputChange(true);
  };

  minInput.addEventListener('change', () => handleInputChange());
  maxInput.addEventListener('change', () => handleInputChange());
  minInput.addEventListener('blur', handleImmediateCommit);
  maxInput.addEventListener('blur', handleImmediateCommit);

  minInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      handleImmediateCommit(event);
    }
  });

  maxInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      handleImmediateCommit(event);
    }
  });

  slider.noUiSlider.on('set', (values) => {
    lastCommittedValues = values.map((value) => alignToStep(Number.parseFloat(value) || 0));
  });
}

function initializeFlowRateSlider(min, max, availableRates, checkboxes, startMin = min, startMax = max) {
  const slider = document.getElementById('flow-rate-slider');
  
  // Destroy existing slider if it exists
  if (slider && slider.noUiSlider) {
    slider.noUiSlider.destroy();
  }
  
  // Create slider
  noUiSlider.create(slider, {
    start: [startMin, startMax],
    connect: true,
    range: { 'min': min, 'max': max },
    step: 0.1,
    format: {
      to: value => parseFloat(value).toFixed(1),
      from: value => parseFloat(value)
    }
  });

  // Handle slider changes - update display in real-time
  slider.noUiSlider.on('update', function(values) {
    const [minVal, maxVal] = values.map(v => parseFloat(v));
    
    // Update display only (no form submission)
    document.getElementById('flow-rate-display').innerHTML = `<span>${minVal}</span> <span>${maxVal}</span> L/min`;
  });

  // Handle slider changes - filter products only when user stops dragging
  slider.noUiSlider.on('set', function(values) {
    const [minVal, maxVal] = values.map(v => parseFloat(v));
    
    // Check/uncheck original checkboxes based on range
    checkboxes.forEach(checkbox => {
      const value = parseFloat(checkbox.value.match(/[\d.]+/)?.[0]) || 0;
      const inRange = value >= minVal && value <= maxVal;
      
      if (checkbox.checked !== inRange) {
        checkbox.checked = inRange;
        // Trigger change event to update Shopify filters
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof HTMLElement ? event.target.closest('.facets__see-results') : null;
  if (!button) return;
  event.preventDefault();
  let facetsForm = button.closest('facets-form-component');
  if (!facetsForm) {
    const targetId = button.getAttribute('data-form-component');
    if (targetId) {
      const targetElement = document.getElementById(targetId);
      if (targetElement instanceof FacetsFormComponent) {
        facetsForm = targetElement;
      } else if (targetElement) {
        const closestComponent = targetElement.closest('facets-form-component');
        if (closestComponent instanceof FacetsFormComponent) {
          facetsForm = closestComponent;
        }
      }
    }
  }
  if (!facetsForm) {
    const fallback = document.querySelector('facets-form-component');
    if (fallback instanceof FacetsFormComponent) {
      facetsForm = fallback;
    }
  }
  if (!(facetsForm instanceof FacetsFormComponent)) return;
  // Use AJAX section rendering instead of full page navigation
  facetsForm.updateFilters();
});

// Show/hide "See Results" buttons based on filter modifications
(function() {
  /**
   * Get the current filter state from a form
   * @param {HTMLFormElement} form - The form element
   * @returns {string} Serialized filter state
   */
  function getFilterState(form) {
    if (!form) return '';

    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (key.startsWith('filter.') || key === 'sort_by') {
        params.append(key, String(value));
      }
    }

    // Sort for consistent comparison
    params.sort();
    return params.toString();
  }

  // Store initial state when page loads
  let initialFormStates = new Map();

  /**
   * Capture initial form states on page load
   */
  function captureInitialStates() {
    const desktopFacets = document.querySelector('.facets--vertical:not(.facets--drawer)');
    if (desktopFacets) {
      const form = desktopFacets.querySelector('form');
      if (form) {
        initialFormStates.set('desktop', getFilterState(form));
      }
    }

    const drawerDialog = document.querySelector('.facets--drawer');
    if (drawerDialog) {
      const form = drawerDialog.querySelector('form');
      if (form) {
        initialFormStates.set('drawer', getFilterState(form));
      }
    }
  }

  /**
   * Update all "See Results" buttons visibility
   */
  function updateAllSeeResultsButtons() {
    // Desktop: button is inside .facets--vertical .facets__actions
    const desktopFacets = document.querySelector('.facets--vertical:not(.facets--drawer)');
    if (desktopFacets) {
      const desktopForm = desktopFacets.querySelector('form');
      const desktopButton = desktopFacets.querySelector('.facets__see-results');

      if (desktopForm && desktopButton) {
        const currentState = getFilterState(desktopForm);
        const initialState = initialFormStates.get('desktop') || '';
        const hasChanges = currentState !== initialState;
        desktopButton.classList.toggle('facets__see-results--visible', hasChanges);
      }
    }

    // Mobile drawer: button is sibling to facets-form-component inside dialog
    const drawerDialog = document.querySelector('.facets--drawer');
    if (drawerDialog) {
      const drawerForm = drawerDialog.querySelector('form');
      const drawerButton = drawerDialog.querySelector('.facets__see-results');

      if (drawerForm && drawerButton) {
        const currentState = getFilterState(drawerForm);
        const initialState = initialFormStates.get('drawer') || '';
        const hasChanges = currentState !== initialState;
        drawerButton.classList.toggle('facets__see-results--visible', hasChanges);
      }
    }
  }

  // Listen for filter changes via the FilterUpdate event
  document.addEventListener(ThemeEvents.FilterUpdate, function() {
    setTimeout(updateAllSeeResultsButtons, 50);
  });

  // Listen for direct checkbox/input changes in facets
  document.addEventListener('change', function(e) {
    const target = e.target;
    if (target && target instanceof HTMLElement && target.closest('.facets')) {
      setTimeout(updateAllSeeResultsButtons, 50);
    }
  });

  // Listen for price slider changes
  document.addEventListener('input', function(e) {
    const target = e.target;
    if (target && target instanceof HTMLElement && target.closest('.facets')) {
      setTimeout(updateAllSeeResultsButtons, 50);
    }
  });

  // Initialize on page load
  document.addEventListener('DOMContentLoaded', function() {
    captureInitialStates();
    updateAllSeeResultsButtons();
  });

  // Update when drawer opens
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        const dialog = mutation.target;
        if (dialog instanceof HTMLElement && dialog.classList.contains('facets--drawer')) {
          setTimeout(updateAllSeeResultsButtons, 100);
        }
      }
    });
  });

  document.addEventListener('DOMContentLoaded', function() {
    const dialogs = document.querySelectorAll('.facets--drawer');
    dialogs.forEach(function(dialog) {
      observer.observe(dialog, { attributes: true });
    });
  });
})();

// =============================================================================
// OR FILTER LOGIC - Enable multi-select for color/swatch filters
// =============================================================================
// Shopify's native filtering uses AND logic for multiple values.
// This implementation enables OR logic by:
// 1. Enabling disabled filter options so users can select multiple
// 2. Intercepting filter submission and fetching products for each value
// 3. Combining results (union) to show products matching ANY selected value
// =============================================================================

(function initializeORFilterLogic() {
  'use strict';

  // Delegate to module-level shared constant and helper
  const shouldUseORLogic = isORStyleFilter;

  // Track if we're currently doing OR filtering
  let isORFilterActive = false;

  /**
   * Process all filter lists on the page — delegates to the unified
   * module-level restoreSiblingOptions() which handles OR-style enabling,
   * cache-aware restore, and visual overrides in one place.
   */
  function processAllFilters() {
    for (const container of getAllFacetsContainers()) {
      restoreSiblingOptions(container);
    }
  }

  /**
   * Parse URL parameters into a map of filter name -> array of values
   * @param {URLSearchParams} params
   * @returns {Map<string, string[]>}
   */
  function parseFilterParams(params) {
    const filterMap = new Map();

    for (const [key, value] of params.entries()) {
      if (!filterMap.has(key)) {
        filterMap.set(key, []);
      }
      filterMap.get(key).push(value);
    }

    return filterMap;
  }

  /**
   * Check if we need OR filtering (multiple values for same OR-logic filter)
   * @param {Map<string, string[]>} filterMap
   * @returns {Object|null} { filterName, values } or null if no OR needed
   */
  function getORFilterInfo(filterMap) {
    for (const [filterName, values] of filterMap.entries()) {
      if (values.length > 1 && shouldUseORLogic(filterName)) {
        return { filterName, values };
      }
    }
    return null;
  }

  /**
   * Build URL for a single filter value
   * @param {Map<string, string[]>} filterMap
   * @param {string} orFilterName
   * @param {string} singleValue
   * @returns {string}
   */
  function buildSingleFilterURL(filterMap, orFilterName, singleValue) {
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, values] of filterMap.entries()) {
      if (key === orFilterName) {
        // Only include the single value for the OR filter
        url.searchParams.append(key, singleValue);
      } else {
        // Include all values for other filters
        values.forEach(v => url.searchParams.append(key, v));
      }
    }

    return url.toString();
  }

  /**
   * Fetch products HTML from a URL
   * @param {string} url
   * @returns {Promise<Document>}
   */
  async function fetchPageDocument(url) {
    const response = await fetch(url);
    const html = await response.text();
    const parser = new DOMParser();
    return parser.parseFromString(html, 'text/html');
  }

  /**
   * Extract product cards from a document with metadata for sorting
   * @param {Document} doc
   * @returns {Map<string, {element: Element, price: number, title: string, position: number}>}
   */
  function extractProductCards(doc) {
    const products = new Map();

    // Try different selectors for product cards
    const selectors = [
      '.product-card',
      '[data-product-card]',
      '.collection-product-card',
      '.product-grid-item',
      'product-card',
      '.grid__item .card'
    ];

    for (const selector of selectors) {
      const cards = doc.querySelectorAll(selector);
      if (cards.length > 0) {
        let position = 0;
        cards.forEach(card => {
          // Try to get product handle from various attributes
          const handle = card.getAttribute('data-product-handle') ||
                        card.getAttribute('data-handle') ||
                        card.querySelector('a[href*="/products/"]')?.href?.match(/\/products\/([^/?]+)/)?.[1] ||
                        card.id;

          if (handle && !products.has(handle)) {
            // Extract price for sorting
            const priceEl = card.querySelector('[data-product-price], .price__regular .price-item, .price .money, .product-card__price');
            let price = 0;
            if (priceEl) {
              const priceText = priceEl.textContent || '';
              const priceMatch = priceText.replace(/[^0-9.]/g, '');
              price = parseFloat(priceMatch) || 0;
            }

            // Extract title for sorting
            const titleEl = card.querySelector('.product-card__title, .card__heading, [data-product-title], h3, h2');
            const title = titleEl?.textContent?.trim() || handle;

            products.set(handle, {
              element: card.cloneNode(true),
              price: price,
              title: title,
              position: position++
            });
          }
        });
        break;
      }
    }

    return products;
  }

  /**
   * Sort products based on current sort order
   * @param {Map<string, {element: Element, price: number, title: string, position: number}>} products
   * @param {string} sortBy - The sort order (e.g., 'price-ascending', 'title-ascending')
   * @returns {Element[]} Sorted array of product card elements
   */
  function sortProducts(products, sortBy) {
    const productArray = Array.from(products.values());

    switch (sortBy) {
      case 'price-ascending':
        productArray.sort((a, b) => a.price - b.price);
        break;
      case 'price-descending':
        productArray.sort((a, b) => b.price - a.price);
        break;
      case 'title-ascending':
        productArray.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-descending':
        productArray.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'created-descending':
      case 'created-ascending':
      case 'best-selling':
      case 'manual':
      default:
        // For these sort types, maintain relative position from first fetch
        // Products are already in position order from extraction
        productArray.sort((a, b) => a.title.localeCompare(b.title));
        break;
    }

    return productArray.map(p => p.element);
  }

  /**
   * Get current sort order from URL or page
   * @returns {string}
   */
  function getCurrentSortOrder() {
    // Check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');
    if (sortBy) return sortBy;

    // Check for selected sort option on page
    const sortSelect = document.querySelector('select[name="sort_by"], [data-sort-by]');
    if (sortSelect && sortSelect.value) return sortSelect.value;

    const sortRadio = document.querySelector('input[name="sort_by"]:checked');
    if (sortRadio && sortRadio.value) return sortRadio.value;

    // Default
    return 'title-ascending';
  }

  /**
   * Find the product grid container
   * @returns {Element|null}
   */
  function findProductGrid() {
    const selectors = [
      '.collection-product-list',
      '.product-grid',
      '#product-grid',
      '[data-product-grid]',
      '.collection__products',
      '.collection-products'
    ];

    for (const selector of selectors) {
      const grid = document.querySelector(selector);
      if (grid) return grid;
    }

    return null;
  }

  /**
   * Update product count display
   * @param {number} count
   */
  function updateProductCount(count) {
    const countElements = document.querySelectorAll(
      '.collection-product-count, [data-product-count], .results-count, .facets__product-count'
    );

    countElements.forEach(el => {
      const text = el.textContent;
      const newText = text.replace(/\d+/, count.toString());
      el.textContent = newText;
    });
  }

  /**
   * Show loading state
   */
  function showLoading() {
    const grid = findProductGrid();
    if (grid) {
      grid.style.opacity = '0.5';
      grid.style.pointerEvents = 'none';
    }
  }

  /**
   * Hide loading state
   */
  function hideLoading() {
    const grid = findProductGrid();
    if (grid) {
      grid.style.opacity = '1';
      grid.style.pointerEvents = 'auto';
    }
  }

  /**
   * Perform OR filtering by fetching products for each value and combining
   * @param {URLSearchParams} params
   */
  async function performORFiltering(params) {
    const filterMap = parseFilterParams(params);
    const orInfo = getORFilterInfo(filterMap);

    if (!orInfo) {
      // No OR filtering needed, proceed normally
      return false;
    }

    isORFilterActive = true;
    showLoading();

    try {
      // Fetch products for each OR filter value
      const fetchPromises = orInfo.values.map(value => {
        const url = buildSingleFilterURL(filterMap, orInfo.filterName, value);
        return fetchPageDocument(url);
      });

      const documents = await Promise.all(fetchPromises);

      // Combine products from all responses (union)
      const combinedProducts = new Map();

      documents.forEach(doc => {
        const products = extractProductCards(doc);
        products.forEach((productData, handle) => {
          if (!combinedProducts.has(handle)) {
            combinedProducts.set(handle, productData);
          }
        });
      });

      // Update the product grid
      const grid = findProductGrid();
      if (grid && combinedProducts.size > 0) {
        // Clear existing products
        grid.innerHTML = '';

        // Sort products based on current sort order
        const sortOrder = getCurrentSortOrder();
        const sortedProducts = sortProducts(combinedProducts, sortOrder);

        // Add sorted products
        sortedProducts.forEach(card => {
          grid.appendChild(card);
        });

        // Update count
        updateProductCount(combinedProducts.size);

        // Update URL without reloading
        const newUrl = new URL(window.location.pathname, window.location.origin);
        for (const [key, value] of params.entries()) {
          newUrl.searchParams.append(key, value);
        }
        history.pushState({ orFilter: true }, '', newUrl.toString());

        // Re-enable filter options
        setTimeout(processAllFilters, 100);

        // Reinitialize any JavaScript functionality on the cards
        if (typeof window.wishlistButtonsInit === 'function') {
          window.wishlistButtonsInit();
        }
      } else if (combinedProducts.size === 0) {
        // Show no results message
        if (grid) {
          grid.innerHTML = '<div class="collection-empty"><p>No products match your selected filters.</p></div>';
        }
        updateProductCount(0);
      }

    } catch (error) {
      console.error('OR Filter error:', error);
      // Fall back to normal navigation
      return false;
    } finally {
      hideLoading();
      isORFilterActive = false;
    }

    return true;
  }

  /**
   * Intercept filter navigation
   */
  /**
   * Handle OR filtering for given params
   * @param {URLSearchParams} params
   * @param {Event} [event] - Optional event to prevent default on
   * @returns {Promise<boolean>} Whether OR filtering was performed
   */
  async function handleORFilterRequest(params, event) {
    const filterMap = parseFilterParams(params);
    const orInfo = getORFilterInfo(filterMap);

    // Only handle if we need OR filtering
    if (!orInfo) {
      return false;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const success = await performORFiltering(params);
    if (!success) {
      // Fall back to normal navigation
      const url = new URL(window.location.pathname, window.location.origin);
      for (const [key, value] of params.entries()) {
        url.searchParams.append(key, value);
      }
      window.location.assign(url.toString());
    }

    return true;
  }

  function interceptFilterNavigation() {
    // Intercept "See Results" button clicks to perform OR filtering
    document.addEventListener('click', async function(event) {
      const button = event.target instanceof HTMLElement
        ? event.target.closest('.facets__see-results')
        : null;

      if (!button) return;

      // Find the facets form
      let facetsForm = button.closest('facets-form-component');
      if (!facetsForm) {
        const targetId = button.getAttribute('data-form-component');
        if (targetId) {
          facetsForm = document.getElementById(targetId);
        }
      }
      if (!facetsForm) {
        facetsForm = document.querySelector('facets-form-component');
      }

      if (!facetsForm || !facetsForm.createURLParameters) return;

      const params = facetsForm.createURLParameters();
      await handleORFilterRequest(params, event);
    }, true); // Use capture phase to intercept before other handlers

    // Intercept auto-apply events for OR filtering
    document.addEventListener('facets:auto-apply', async function(event) {
      const { params } = event.detail;
      if (!params) return;

      const handled = await handleORFilterRequest(params, event);

      // If OR filtering handled it, prevent default navigation
      if (handled) {
        event.preventDefault();
      }
    });
  }

  // Initialize on page load
  document.addEventListener('DOMContentLoaded', function() {
    processAllFilters();
    interceptFilterNavigation();

    // Watch for DOM changes
    const observer = new MutationObserver(function(mutations) {
      let shouldProcess = false;

      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          const target = mutation.target;
          if (target instanceof HTMLInputElement && target.type === 'checkbox') {
            const filterList = target.closest('[id^="filters-list-"]');
            if (filterList) {
              const filterName = filterList.getAttribute('name') || filterList.id || '';
              if (shouldUseORLogic(filterName)) {
                shouldProcess = true;
              }
            }
          }
        }

        if (mutation.type === 'childList') {
          const target = mutation.target;
          if (target instanceof HTMLElement) {
            if (target.classList.contains('facets__inputs-list') ||
                target.closest('.facets__inputs-list') ||
                target.closest('facet-inputs-component')) {
              shouldProcess = true;
            }
          }
        }
      });

      if (shouldProcess && !isORFilterActive) {
        setTimeout(processAllFilters, 50);
      }
    });

    const facetsContainers = document.querySelectorAll('.facets, facets-form-component');
    facetsContainers.forEach(container => {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'class']
      });
    });
  });

  // Re-enable options after filter updates
  if (typeof ThemeEvents !== 'undefined' && ThemeEvents.FilterUpdate) {
    document.addEventListener(ThemeEvents.FilterUpdate, function() {
      if (!isORFilterActive) {
        setTimeout(processAllFilters, 100);
        setTimeout(processAllFilters, 300);
        setTimeout(processAllFilters, 600);
      }
    });
  }

  // Handle browser back/forward
  window.addEventListener('popstate', function() {
    setTimeout(processAllFilters, 100);
  });

  // Export for debugging
  window.ORFilterLogic = {
    processAllFilters,
    restoreSiblingOptions,
    shouldUseORLogic,
    performORFiltering,
    OR_FILTER_PATTERNS
  };
})();
